'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { contentApi } from '@/lib/api';
import { Sparkles, Loader2, Upload, Copy, Check, Film, Clapperboard } from 'lucide-react';

interface BRollPrompt {
  scene_number: number;
  scene_description: string;
  broll_prompt: string;
  search_keywords: string[];
  duration_sec: number;
}

interface BRollSelection {
  path: string;
  filename: string;
  source: 'upload' | 'generated';
}

interface BRollSectionProps {
  script: string;
  videoPrompt: string;
  projectId: string;
  contentType: string;
  compositionId?: string;
  postId?: string | null;
}

export function BRollSection({ script, videoPrompt, projectId, contentType, compositionId, postId }: BRollSectionProps) {
  const [prompts, setPrompts] = useState<BRollPrompt[]>(() => {
    if (typeof window !== 'undefined') {
      try { const s = localStorage.getItem('cw-brollPrompts'); return s ? JSON.parse(s) : []; } catch { return []; }
    }
    return [];
  });
  const [selections, setSelections] = useState<Record<number, BRollSelection>>(() => {
    if (typeof window !== 'undefined') {
      try { const s = localStorage.getItem('cw-brollSelections'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    }
    return {};
  });
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [uploadingScene, setUploadingScene] = useState<number | null>(null);
  const [generatingScene, setGeneratingScene] = useState<number | null>(null);
  const [genErrors, setGenErrors] = useState<Record<number, string>>({});
  const [pushingToRemotion, setPushingToRemotion] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // Load from DB if localStorage is empty
  const loadedFromDb = useRef(false);
  useEffect(() => {
    if (loadedFromDb.current || !postId || prompts.length > 0) return;
    loadedFromDb.current = true;
    fetch(`/api/content/posts/${postId}`)
      .then(r => r.json())
      .then(post => {
        if (post.broll_prompts) {
          try { const p = JSON.parse(post.broll_prompts); setPrompts(p); localStorage.setItem('cw-brollPrompts', JSON.stringify(p)); } catch {}
        }
        if (post.broll_selections) {
          try { const s = JSON.parse(post.broll_selections); setSelections(s); localStorage.setItem('cw-brollSelections', JSON.stringify(s)); } catch {}
        }
      }).catch(() => {});
  }, [postId, prompts.length]);

  const hasScript = !!(script || videoPrompt);

  const savePrompts = (p: BRollPrompt[]) => {
    setPrompts(p);
    localStorage.setItem('cw-brollPrompts', JSON.stringify(p));
    if (postId) {
      contentApi.update(postId, { brollPrompts: JSON.stringify(p) }).catch(() => {});
    }
  };

  const saveSelections = (s: Record<number, BRollSelection>) => {
    setSelections(s);
    localStorage.setItem('cw-brollSelections', JSON.stringify(s));
    if (postId) {
      contentApi.update(postId, { brollSelections: JSON.stringify(s) }).catch(() => {});
    }
  };

  const [genMessage, setGenMessage] = useState('');

  const generateBRollPrompts = async () => {
    if (!hasScript) { setGenMessage('⚠️ Add a script or video prompt in Step 2 first.'); return; }
    if (!projectId) { setGenMessage('⚠️ No project selected.'); return; }
    setGenerating(true);
    setGenMessage('🎬 Generating B-roll prompts... (may take 30-90s due to gateway queue)');
    try {
      const res = await fetch('/api/content/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field: 'broll',
          currentValue: script || videoPrompt,
          context: { script, videoPrompt },
          projectId,
          contentType,
        }),
      });
      const data = await res.json();
      if (data.generated) {
        let raw = data.generated.trim();
        if (raw.startsWith('```')) {
          raw = raw.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '').trim();
        }
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            savePrompts(parsed);
            setGenMessage(`✅ Generated ${parsed.length} B-roll prompts!`);
          } else {
            setGenMessage('⚠️ AI returned unexpected format. Try again.');
          }
        } catch {
          console.error('[broll] Failed to parse AI response:', raw?.slice(0, 200));
          setGenMessage('⚠️ Failed to parse response. Try again.');
        }
      } else {
        setGenMessage('⚠️ No response from AI. Gateway may be busy — try again.');
      }
    } catch (err) {
      console.error('[broll] Generation error:', err);
      setGenMessage('❌ Generation failed. Check console for details.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = useCallback(async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  const handleFileUpload = async (sceneNum: number, file: File) => {
    setUploadingScene(sceneNum);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('sceneNumber', String(sceneNum));
      const res = await fetch('/api/content/broll/upload', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.ok) {
        const newSelections = { ...selections, [sceneNum]: { path: data.path, filename: data.filename || file.name, source: 'upload' as const } };
        saveSelections(newSelections);
      }
    } catch (err) {
      console.error('[broll] Upload error:', err);
    } finally {
      setUploadingScene(null);
    }
  };

  const pushToRemotion = async () => {
    const clips = Object.values(selections);
    if (!clips.length) { setPushMessage('⚠️ No clips selected. Generate or upload B-roll for each scene first.'); return; }
    setPushingToRemotion(true);
    setPushMessage('🚀 Pushing clips to Remotion...');
    try {
      let copied = 0;
      for (const sel of clips) {
        const res = await fetch('/api/content/broll/push-remotion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clipPath: sel.path, composition: compositionId }),
        });
        const data = await res.json();
        if (data.ok) copied++;
        else { setPushMessage(`❌ ${data.error}`); setPushingToRemotion(false); return; }
      }
      setPushMessage(`✅ ${copied} clips pushed to Remotion! Use <OffthreadVideo src={staticFile("video/filename.mp4")} /> in your scenes.`);
    } catch { setPushMessage('❌ Push failed.'); }
    finally { setPushingToRemotion(false); }
  };

  const generateClip = async (sceneNum: number, prompt: string, durationSec: number) => {
    setGeneratingScene(sceneNum);
    setGenErrors(prev => ({ ...prev, [sceneNum]: '' }));
    try {
      const res = await fetch('/api/content/broll/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          aspectRatio: contentType === 'reel' ? '9:16' : '16:9',
          durationSec,
        }),
      });
      const data = await res.json();
      if (data.ok && data.path) {
        const newSelections = { ...selections, [sceneNum]: { path: data.path, filename: data.filename, source: 'generated' as const } };
        saveSelections(newSelections);
      } else if (res.status === 501) {
        setGenErrors(prev => ({ ...prev, [sceneNum]: `⚙️ AI video generation not configured yet: ${data.error}. Copy the prompt above into Kling/Higgsfield and upload the clip below.` }));
      } else {
        setGenErrors(prev => ({ ...prev, [sceneNum]: `❌ ${data.error || 'Generation failed.'}` }));
      }
    } catch (err) {
      console.error('[broll] Generate error:', err);
      setGenErrors(prev => ({ ...prev, [sceneNum]: '❌ Generation failed. Check console for details.' }));
    } finally {
      setGeneratingScene(null);
    }
  };

  if (contentType !== 'video' && contentType !== 'reel') return null;

  return (
    <div className="border-t border-gray-700 pt-4 space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-400 font-medium flex items-center gap-2">
          <Film className="w-4 h-4" /> 🎬 B-Roll Clips
        </label>
        <button
          onClick={generateBRollPrompts}
          disabled={generating || !hasScript}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:shadow-[0_0_8px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          title={!hasScript ? 'Add a script or video prompt first (Step 2)' : 'Generate B-roll prompts from your script'}
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {generating ? 'Generating...' : '✨ Generate B-Roll Prompts'}
        </button>
      </div>

      {genMessage && <p className="text-sm text-gray-300">{genMessage}</p>}

      {!hasScript && !genMessage && (
        <p className="text-xs text-gray-500">Add a script or video prompt in Step 2 to generate B-roll suggestions.</p>
      )}

      {prompts.length > 0 && (
        <div className="space-y-3">
          {prompts.map((p) => (
            <div
              key={p.scene_number}
              className="rounded-lg border border-gray-700 bg-white/[0.02] p-4 space-y-3"
            >
              {/* Scene header */}
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">
                  Scene {p.scene_number}: <span className="text-gray-400">{p.scene_description}</span>
                </h4>
                <span className="text-xs text-gray-500">{p.duration_sec}s</span>
              </div>

              {/* AI Prompt */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">AI Prompt:</p>
                <div className="bg-white/5 rounded-lg p-3 text-sm text-gray-300">
                  {p.broll_prompt}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(p.broll_prompt, `kling-${p.scene_number}`)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
                  >
                    {copiedId === `kling-${p.scene_number}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedId === `kling-${p.scene_number}` ? 'Copied!' : '📋 Copy for Kling'}
                  </button>
                  <button
                    onClick={() => copyToClipboard(p.broll_prompt, `higgs-${p.scene_number}`)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-purple-400 bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-all"
                  >
                    {copiedId === `higgs-${p.scene_number}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedId === `higgs-${p.scene_number}` ? 'Copied!' : '📋 Copy for Higgsfield'}
                  </button>
                </div>
              </div>

              {/* AI Generate */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">🎥 AI Generate:</p>
                <button
                  onClick={() => generateClip(p.scene_number, p.broll_prompt, p.duration_sec)}
                  disabled={generatingScene === p.scene_number}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-all disabled:opacity-50"
                >
                  {generatingScene === p.scene_number ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clapperboard className="w-3 h-3" />}
                  {generatingScene === p.scene_number ? 'Generating...' : '✨ Generate Clip'}
                </button>
                {genErrors[p.scene_number] && (
                  <p className="text-xs text-gray-400">{genErrors[p.scene_number]}</p>
                )}
              </div>

              {/* Upload */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">📤 Upload Clip:</p>
                {selections[p.scene_number] ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/5 border border-green-500/20">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-400">{selections[p.scene_number].filename}</span>
                    <button
                      onClick={() => {
                        const newSel = { ...selections };
                        delete newSel[p.scene_number];
                        saveSelections(newSel);
                      }}
                      className="ml-auto text-xs text-gray-500 hover:text-red-400"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRefs.current[p.scene_number]?.click()}
                    className="border border-dashed border-gray-700 rounded-lg p-3 text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
                  >
                    {uploadingScene === p.scene_number ? (
                      <div className="flex items-center justify-center gap-2 text-cyan-400">
                        <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                      </div>
                    ) : (
                      <div className="text-gray-500 flex items-center justify-center gap-2">
                        <Upload className="w-4 h-4" />
                        <span className="text-xs">Drop video clip or click to browse</span>
                      </div>
                    )}
                  </div>
                )}
                <input
                  ref={(el) => { fileInputRefs.current[p.scene_number] = el; }}
                  type="file"
                  accept="video/*"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileUpload(p.scene_number, f);
                  }}
                />
              </div>
            </div>
          ))}

          {/* Push to Remotion */}
          {Object.keys(selections).length > 0 && (
            <div className="mt-4 p-4 rounded-lg border border-cyan-500/20 bg-cyan-500/5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">🎬 {Object.keys(selections).length} clip{Object.keys(selections).length > 1 ? 's' : ''} selected</p>
                  <p className="text-xs text-gray-400">Push to Remotion to use in your video composition</p>
                </div>
                <button
                  onClick={pushToRemotion}
                  disabled={pushingToRemotion}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:shadow-[0_0_12px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50"
                >
                  {pushingToRemotion ? <Loader2 className="w-4 h-4 animate-spin" /> : '🚀'} Push to Remotion
                </button>
              </div>
              {pushMessage && <p className="text-sm text-gray-300">{pushMessage}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
